import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { computePunchPending } from "@/lib/punchPending";
import { useKmAlertGate } from "@/components/KmAlertGate";
import { useHolidayChecker } from "@/lib/useHolidays";
import api, { apiError } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { useBase } from "@/context/BaseContext";
import { Loading, ErrorState, Empty } from "@/components/DataState";
import { fmtDate, num, km } from "@/lib/format";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, X, Trash2, CalendarRange, ShieldCheck, Clock } from "lucide-react";

const dkey = (v) => String(v || "").slice(0, 10);
const today = () => new Date().toISOString().slice(0, 10);
const localToday = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const isOld = (dt) => !!dt && String(dt).slice(0, 10) < localToday();

function Shell({ title, description, loading, error, onRetry, onClose, children }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl border-slate-800 bg-slate-900" data-testid="pending-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-100">{title}</DialogTitle>
          <DialogDescription className="text-slate-400">{description}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[70vh] overflow-y-auto pr-1">
          {loading ? <Loading /> : error ? <ErrorState error={error} onRetry={onRetry} /> : children}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DateGroups({ items, dateOf, renderRow }) {
  const groups = useMemo(() => {
    const m = {};
    items.forEach((it) => {
      const d = dkey(dateOf(it));
      (m[d] = m[d] || []).push(it);
    });
    return Object.entries(m).sort((a, b) => b[0].localeCompare(a[0]));
  }, [items, dateOf]);

  if (!items.length) return <Empty label="Nenhuma pendência" />;
  return (
    <div className="space-y-4">
      {groups.map(([d, rows]) => (
        <div key={d} data-testid={`pending-group-${d}`}>
          <div className="mb-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
            <CalendarRange className="h-3.5 w-3.5" /> {fmtDate(d)}
            <span className="text-slate-600">· {rows.length}</span>
          </div>
          <div className="space-y-2">{rows.map(renderRow)}</div>
        </div>
      ))}
    </div>
  );
}

function RowCard({ children }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-800 bg-slate-950/50 px-3 py-2">
      {children}
    </div>
  );
}

/* ---- Faltas ---- */
function FaltasPending({ onClose, onChanged, old }) {
  const { data, loading, error, refetch } = useFetch("/admin/faltas");
  const [busy, setBusy] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const pend = useMemo(() => (data?.faltas || []).filter((f) => f.status === "pending" && (!old || isOld(f.date))), [data, old]);

  const act = async (f, decision) => {
    setBusy(f.id);
    try {
      await api.post(`/admin/faltas/${f.id}/review`, { decision, discount_amount: null });
      toast.success(decision === "approve" ? "Falta aprovada" : "Falta zerada");
      setConfirmId(null);
      await refetch();
      onChanged && onChanged();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Shell title={old ? "Faltas sem resolução (dias anteriores)" : "Faltas a resolver"} description="Justificativas pendentes, separadas por data. Atualizar aprova; Zerar reprova a falta." loading={loading} error={error} onRetry={refetch} onClose={onClose}>
      <DateGroups
        items={pend}
        dateOf={(f) => f.date}
        renderRow={(f) => (
          <RowCard key={f.id}>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-slate-100">{f.user_name}</p>
              <p className="truncate text-xs capitalize text-slate-500">
                {f.role}{f.vehicle_type ? ` · ${f.vehicle_type}` : ""}{f.justification_type ? " · com justificativa" : " · sem justificativa"}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-1.5" data-testid={`pending-falta-${f.id}`}>
              {confirmId === f.id ? (
                <>
                  <span className="text-xs text-rose-300">Zerar?</span>
                  <Button size="sm" className="h-8 bg-rose-600 px-2 text-white hover:bg-rose-700" disabled={busy === f.id} onClick={() => act(f, "reject")} data-testid={`pending-falta-zero-confirm-${f.id}`}><Check className="h-4 w-4" /></Button>
                  <Button size="sm" variant="outline" className="h-8 border-slate-700 px-2 text-slate-300" onClick={() => setConfirmId(null)} data-testid={`pending-falta-zero-cancel-${f.id}`}><X className="h-4 w-4" /></Button>
                </>
              ) : (
                <>
                  <Button size="sm" className="h-8 bg-emerald-600 px-2 text-white hover:bg-emerald-700" disabled={busy === f.id} onClick={() => act(f, "approve")} data-testid={`pending-falta-update-${f.id}`}><Check className="h-4 w-4" /> Atualizar</Button>
                  <Button size="sm" variant="outline" className="h-8 border-slate-700 px-2 text-rose-300 hover:border-rose-500/40" onClick={() => setConfirmId(f.id)} data-testid={`pending-falta-zero-${f.id}`}><Trash2 className="h-4 w-4" /> Zerar</Button>
                </>
              )}
            </div>
          </RowCard>
        )}
      />
    </Shell>
  );
}

/* ---- Cadastros pendentes (aprovações) ---- */
function ApprovalsPending({ onClose, onChanged }) {
  const { selectedBaseId } = useBase();
  const { data, loading, error, refetch } = useFetch("/admin/pending-funcionarios");
  const [busy, setBusy] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const pend = useMemo(() => (data?.pending || []).filter((p) => !selectedBaseId || (p.base_ids || []).includes(selectedBaseId)), [data, selectedBaseId]);

  const act = async (p, action) => {
    setBusy(p.id);
    try {
      await api.post(`/admin/funcionarios/${p.id}/${action}`, {});
      toast.success(action === "approve" ? "Cadastro aprovado" : "Cadastro rejeitado");
      setConfirmId(null);
      await refetch();
      onChanged && onChanged();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Shell title="Cadastros pendentes" description="Funcionários aguardando aprovação, separados por data. Atualizar aprova; Zerar rejeita." loading={loading} error={error} onRetry={refetch} onClose={onClose}>
      <DateGroups
        items={pend}
        dateOf={(p) => p.created_at || today()}
        renderRow={(p) => (
          <RowCard key={p.id}>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-slate-100">{p.name || p.email || "—"}</p>
              <p className="truncate text-xs capitalize text-slate-500">{p.role || "funcionário"}{p.vehicle_type ? ` · ${p.vehicle_type}` : ""}</p>
            </div>
            <div className="flex shrink-0 items-center gap-1.5" data-testid={`pending-approval-${p.id}`}>
              {confirmId === p.id ? (
                <>
                  <span className="text-xs text-rose-300">Rejeitar?</span>
                  <Button size="sm" className="h-8 bg-rose-600 px-2 text-white hover:bg-rose-700" disabled={busy === p.id} onClick={() => act(p, "reject")} data-testid={`pending-approval-zero-confirm-${p.id}`}><Check className="h-4 w-4" /></Button>
                  <Button size="sm" variant="outline" className="h-8 border-slate-700 px-2 text-slate-300" onClick={() => setConfirmId(null)} data-testid={`pending-approval-zero-cancel-${p.id}`}><X className="h-4 w-4" /></Button>
                </>
              ) : (
                <>
                  <Button size="sm" className="h-8 bg-emerald-600 px-2 text-white hover:bg-emerald-700" disabled={busy === p.id} onClick={() => act(p, "approve")} data-testid={`pending-approval-update-${p.id}`}><Check className="h-4 w-4" /> Atualizar</Button>
                  <Button size="sm" variant="outline" className="h-8 border-slate-700 px-2 text-rose-300 hover:border-rose-500/40" onClick={() => setConfirmId(p.id)} data-testid={`pending-approval-zero-${p.id}`}><Trash2 className="h-4 w-4" /> Zerar</Button>
                </>
              )}
            </div>
          </RowCard>
        )}
      />
    </Shell>
  );
}

/* ---- Auditorias de rota ---- */
function AuditsPending({ drivers, onClose, onChanged, old }) {
  const { selectedBaseId } = useBase();
  const { data, loading, error, refetch } = useFetch("/admin/routes");
  const [busy, setBusy] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const nameOf = useMemo(() => {
    const m = {};
    (drivers || []).forEach((d) => { m[d.id] = d.name; });
    return m;
  }, [drivers]);
  const pend = useMemo(() => (data?.routes || []).filter((r) => (r.audit_status || "pending") === "pending" && (!selectedBaseId || nameOf[r.driver_id]) && (!old || isOld(r.start_time || r.end_time))), [data, selectedBaseId, nameOf, old]);

  const [gate, kmAlertDialog] = useKmAlertGate(data?.routes);
  const validate = (r) => gate(r, null, async (info) => {
    setBusy(r.id);
    try {
      await api.post(`/admin/routes/${r.id}/audit`, info ? { approved: true, km_alert: true, km_alert_avg: Math.round(info.avg * 10) / 10 } : { approved: true });
      toast.success(info ? "Auditoria validada com alerta de km" : "Auditoria validada");
      setConfirmId(null);
      await refetch();
      onChanged && onChanged();
      return true;
    } catch (e) {
      toast.error(apiError(e));
      return false;
    } finally {
      setBusy(null);
    }
  });

  return (
    <Shell title={old ? "Rotas sem KM validada (dias anteriores)" : "Auditorias de rota"} description="Quilometragens aguardando validação, separadas por data. Atualizar valida a auditoria." loading={loading} error={error} onRetry={refetch} onClose={onClose}>
      <DateGroups
        items={pend}
        dateOf={(r) => r.start_time || r.end_time}
        renderRow={(r) => (
          <RowCard key={r.id}>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-slate-100">{nameOf[r.driver_id] || "Entregador"}</p>
              <p className="truncate text-xs text-slate-500">{km(r.corrected_km ?? r.km)} · odôm. {num(r.start_odometer)}→{num(r.end_odometer)}</p>
            </div>
            <div className="flex shrink-0 items-center gap-1.5" data-testid={`pending-audit-${r.id}`}>
              {confirmId === r.id ? (
                <>
                  <span className="text-xs text-emerald-300">Validar?</span>
                  <Button size="sm" className="h-8 bg-emerald-600 px-2 text-white hover:bg-emerald-700" disabled={busy === r.id} onClick={() => validate(r)} data-testid={`pending-audit-confirm-${r.id}`}><Check className="h-4 w-4" /></Button>
                  <Button size="sm" variant="outline" className="h-8 border-slate-700 px-2 text-slate-300" onClick={() => setConfirmId(null)} data-testid={`pending-audit-cancel-${r.id}`}><X className="h-4 w-4" /></Button>
                </>
              ) : (
                <Button size="sm" className="h-8 bg-emerald-600 px-2 text-white hover:bg-emerald-700" disabled={busy === r.id} onClick={() => setConfirmId(r.id)} data-testid={`pending-audit-update-${r.id}`}><ShieldCheck className="h-4 w-4" /> Atualizar</Button>
              )}
            </div>
          </RowCard>
        )}
      />
      {kmAlertDialog}
    </Shell>
  );
}

/* ---- Entregas do dia ---- */
function DeliveryRow({ d, onChanged, date, dayCount }) {
  const [val, setVal] = useState(String((date ? dayCount : d.deliveries_today) || ""));
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const tid = date ? `${d.id}-${date}` : d.id;

  const save = async (count) => {
    setBusy(true);
    try {
      await api.post("/admin/deliveries/history", { driver_id: d.id, date: date || today(), count });
      toast.success(count === 0 ? "Entregas zeradas" : "Entregas atualizadas");
      setConfirm(false);
      onChanged && onChanged();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <RowCard>
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-slate-100">{d.name}</p>
        <p className="truncate text-xs capitalize text-slate-500">{d.vehicle_type || "entregador"} · {date ? `no dia: ${num(dayCount || 0)}` : `hoje: ${num(d.deliveries_today)}`}</p>
      </div>
      <div className="flex shrink-0 items-center gap-1.5" data-testid={`pending-delivery-${tid}`}>
        {confirm ? (
          <>
            <span className="text-xs text-rose-300">Zerar?</span>
            <Button size="sm" className="h-8 bg-rose-600 px-2 text-white hover:bg-rose-700" disabled={busy} onClick={() => save(0)} data-testid={`pending-delivery-zero-confirm-${tid}`}><Check className="h-4 w-4" /></Button>
            <Button size="sm" variant="outline" className="h-8 border-slate-700 px-2 text-slate-300" onClick={() => setConfirm(false)} data-testid={`pending-delivery-zero-cancel-${tid}`}><X className="h-4 w-4" /></Button>
          </>
        ) : (
          <>
            <Input type="number" min="0" value={val} onChange={(e) => setVal(e.target.value)} placeholder="qtd" data-testid={`pending-delivery-input-${tid}`} className="h-8 w-20 border-slate-700 bg-slate-950 text-slate-100" />
            <Button size="sm" className="h-8 bg-emerald-600 px-2 text-white hover:bg-emerald-700" disabled={busy} onClick={() => save(Math.max(0, parseInt(val, 10) || 0))} data-testid={`pending-delivery-update-${tid}`}><Check className="h-4 w-4" /> Atualizar</Button>
            <Button size="sm" variant="outline" className="h-8 border-slate-700 px-2 text-rose-300 hover:border-rose-500/40" onClick={() => setConfirm(true)} data-testid={`pending-delivery-zero-${tid}`}><Trash2 className="h-4 w-4" /> Zerar</Button>
          </>
        )}
      </div>
    </RowCard>
  );
}

function DeliveriesPending({ drivers, onClose, onChanged }) {
  const pend = useMemo(() => (drivers || []).filter((d) => !d.finalized_today), [drivers]);

  return (
    <Shell title="Entregas do dia" description="Entregadores sem registro finalizado hoje. Informe a quantidade (Atualizar) ou registre 0 (Zerar)." loading={false} error={null} onRetry={() => {}} onClose={onClose}>
      <DateGroups
        items={pend.map((d) => ({ ...d, _date: today() }))}
        dateOf={() => today()}
        renderRow={(d) => <DeliveryRow key={d.id} d={d} onChanged={onChanged} />}
      />
    </Shell>
  );
}

/* ---- Pendências de ponto (dias anteriores): colaboradores sem ponto + batidas pendentes ---- */
function PunchPendingPanel({ windowStart, onClose, onChanged }) {
  const { data, loading, error, refetch } = useFetch("/admin/punch-audit");
  const [busy, setBusy] = useState(null);
  const navigate = useNavigate();
  const isHoliday = useHolidayChecker();
  const { pending, missing } = useMemo(() => computePunchPending(data, windowStart, isHoliday), [data, windowStart, isHoliday]);
  const review = async (it, approved) => {
    setBusy(`${it.id}-${approved}`);
    try {
      await api.post(`/admin/punch-audit/${it.kind}/${it.id}`, { approved });
      toast.success(approved ? "Ponto aprovado" : "Ponto reprovado");
      await refetch();
      onChanged && onChanged();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };
  const typeLabel = (it) => { const t = String(it.punch_type || it.type || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase(); return t === "entrada" ? "Entrada" : t === "saida" ? "Saída" : t; };
  const section = (title, n, testId, body) => (
    <div data-testid={testId}>
      <div className="mb-2 flex items-center gap-2 border-b border-slate-800 pb-1.5">
        <p className="text-sm font-semibold text-slate-200">{title}</p>
        <span className={`ml-auto rounded-full px-2 py-0.5 text-xs font-bold ${n > 0 ? "bg-rose-500/15 text-rose-300" : "bg-slate-800 text-slate-500"}`}>{n}</span>
      </div>
      {body}
    </div>
  );
  return (
    <Shell title="Pendências de ponto" description="Dias anteriores. Somente colaboradores com ponto obrigatório (entregadores, fins de semana e feriados cadastrados não entram)." loading={loading} error={error} onRetry={refetch} onClose={onClose}>
      <div className="space-y-6">
        {section("Colaboradores sem ponto", missing.length, "punch-missing-section", (
          <DateGroups
            items={missing}
            dateOf={(p) => p._date}
            renderRow={(p) => (
              <RowCard key={p._key}>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-100">{p.name}</p>
                  <p className="truncate text-xs capitalize text-slate-500">{p.category} · nenhuma batida no dia</p>
                </div>
                <Button size="sm" variant="outline" className="h-8 border-slate-700 px-2 text-slate-300" onClick={() => { onClose(); navigate("/ponto"); }} data-testid={`pending-missing-punch-${p._key}`}>
                  <Clock className="h-4 w-4" /> Registrar ponto
                </Button>
              </RowCard>
            )}
          />
        ))}
        {section("Batidas de ponto pendentes", pending.length, "punch-pending-section", (
          <DateGroups
            items={pending}
            dateOf={(it) => it.date || it.timestamp}
            renderRow={(it) => (
              <RowCard key={`${it.kind}-${it.id}`}>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-100">{it.person_name}</p>
                  <p className="truncate text-xs capitalize text-slate-500">{it.category} · {typeLabel(it)}{it.timestamp ? ` · ${new Date(it.timestamp).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}` : ""}{it.is_manual || it.manual ? " · manual" : ""}</p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5" data-testid={`pending-punch-${it.id}`}>
                  <Button size="sm" className="h-8 bg-emerald-600 px-2 text-white hover:bg-emerald-700" disabled={busy === `${it.id}-true`} onClick={() => review(it, true)} data-testid={`pending-punch-approve-${it.id}`}><Check className="h-4 w-4" /> Aprovar</Button>
                  <Button size="sm" variant="outline" className="h-8 border-slate-700 px-2 text-rose-300 hover:border-rose-500/40" disabled={busy === `${it.id}-false`} onClick={() => review(it, false)} data-testid={`pending-punch-reject-${it.id}`}><X className="h-4 w-4" /> Reprovar</Button>
                </div>
              </RowCard>
            )}
          />
        ))}
      </div>
    </Shell>
  );
}

export function PendingDetailDialog({ item, drivers, onClose, onChanged, windowStart }) {
  if (!item) return null;
  const common = { onClose, onChanged };
  if (item.key === "rotas_sem_km_validada") return <AuditsPending drivers={drivers} old {...common} />;
  if (item.key === "faltas_sem_resolucao") return <FaltasPending old {...common} />;
  if (item.key === "pontos") return <PunchPendingPanel windowStart={windowStart} {...common} />;
  if (item.key === "faltas") return <FaltasPending {...common} />;
  if (item.key === "approvals") return <ApprovalsPending {...common} />;
  if (item.key === "audits") return <AuditsPending drivers={drivers} {...common} />;
  if (item.key === "deliveries") return <DeliveriesPending drivers={drivers} {...common} />;
  return null;
}
